/**
 * useItemsConditions
 * ------------------
 * This composable centralizes and encapsulates all dynamic condition checks
 * used to determine the visibility of account dashboard items based on:
 *
 * - User roles from Vuex (`getMe`)
 * - Vuex store state (e.g., tenant permissions)
 * - TokenService-based feature flags (via `.userHasPermission`)
 * - User's group membership (e.g., First Stop Group)
 *
 * It returns named boolean functions such as `canSeeProfile` etc.
 * which are used by the account item matrix or other permission-driven UI features.
 */
import { computed } from "vue";
import { useStore } from "vuex";
import TokenService from "@/services/storage.service";
import { useI18n } from "vue-i18n";

export function useItemsConditions() {
  const store = useStore();
  const { t } = useI18n();
  const me = computed(() => store.getters.getMe);
  const tenant = computed(() => store.getters.tenantId);
  const tenantPermissions = computed(
    () => store.getters.getTenantPermissions || [],
  );

  const userRoles = computed(() => me.value?.roles.map((r) => r.name) || []);

  // --- Dynamic conditions with tenant + permission checks ---
  const canSeeAboutPage = () => isModuleVisible(null, "aboutPage.see");

  const isModuleActiveOrHasPermission = (moduleName, permissionName) => {
    if (moduleName && !tenantPermissions.value.includes(moduleName)) {
      return false; // Module is disabled, never show
    }

    if (moduleName && tenantPermissions.value.includes(moduleName)) {
      if (permissionName) {
        return TokenService.userHasPermission(permissionName);
      }
      return true;
    }

    return permissionName
      ? TokenService.userHasPermission(permissionName)
      : false;
  };

  // --- Role checks ---
  const hasAllowedRole = (allowedRoles = []) => {
    return (
      allowedRoles.length === 0 ||
      allowedRoles.some((role) => userRoles.value.includes(role))
    );
  };

  const isModuleVisible = (moduleName, userPermission) => {
    const hasModule =
      moduleName === null || tenantPermissions.value.includes(moduleName);
    const hasUserPermission =
      userPermission === null ||
      (userPermission != null &&
        TokenService.userHasPermission(userPermission));
    return hasModule && hasUserPermission;
  };

  const canSeeBTraining = () =>
    isModuleActiveOrHasPermission("onlineTrainings", "bTraining.see");
  const canSeeDisplaysFurniture = () =>
    isModuleVisible(null, "displaysFurniture.see");
  const canSeeExternalTraining = () =>
    isModuleActiveOrHasPermission("externalTraining", "externalTraining.see");
  const canSeeIndividualSupport = () =>
    isModuleVisible(null, "individualSupport.see");
  const canSeeKickbackExchange = () =>
    isModuleVisible(null, "kickbackExchange.see");
  const canSeeKickbackFeeBox = () =>
    isModuleVisible(null, "kickBackFeeBox.see") &&
    hasAllowedRole(["dealer", "staff"]);
  const canSeeMainPageDefaultBanners = () =>
    isModuleActiveOrHasPermission("mediaBanners", "mainPage.defaultBanners");
  const canSeeMediaDownload = () =>
    isModuleActiveOrHasPermission("media", "mediaDownload.see");
  const canSeePriceBrochures = () =>
    isModuleActiveOrHasPermission("brochure-prices", "mediaDownload.see");
  const canSeeOnlineTraining = () =>
    isModuleActiveOrHasPermission("onlineTrainings", "onlineTraining.see");
  const canSeeProfile = () =>
    isModuleActiveOrHasPermission("myProfile", "profilePage.see") &&
    hasAllowedRole(["dealer"]);
  // shop
  const canSeeShop = () => isModuleVisible("shop", null);
  const canSeeShopComingSoon = () =>
    isModuleActiveOrHasPermission("shop", "shopComingSoon.see");
  const canSeeShopOrders = () =>
    isModuleActiveOrHasPermission("shop", "orderList.see") &&
    hasAllowedRole(["dealer"]);
  const canSeeShopTeaser = () =>
    isModuleActiveOrHasPermission("shop", "shopTeaser.see") &&
    hasAllowedRole(["dealer", "staff"]);
  const canSeeCartIcon = () =>
    isModuleActiveOrHasPermission("shop", "cartIcon.see") &&
    hasAllowedRole(["dealer"]);

  // points
  const canSeePointsBox = () =>
    isModuleActiveOrHasPermission("points", "pointBox.see") &&
    hasAllowedRole(["dealer", "staff"]);
  const canSeePointsInfo = () =>
    isModuleActiveOrHasPermission("points", "pointsInfo.see") &&
    hasAllowedRole(["dealer"]);

  const canSeeUberall = () =>
    isModuleActiveOrHasPermission("uberall", "uberall.see");
  const canSeeWelcomePoints = () => isModuleVisible(null, "welcomePoints.see");
  const canUsePointsInExternalTraining = () =>
    isModuleVisible(null, "externalTraining.usePoints");
  const canSeeSharePointsBtn = () =>
    isModuleActiveOrHasPermission("sharePoints", "share.points");
  const isFirstStopGroup = () =>
    isModuleActiveOrHasPermission("commitmentLetter", "firstStop.see") &&
    hasAllowedRole(["dealer"]);

  // --- Tenant-only based checks ---
  const isUnknownHiddenByModuleConfig = (moduleSlug, configKey) =>
    store.getters.isModuleConfigEnabled(moduleSlug, configKey) &&
    store.getters.getIsUnknown;

  const canSeeApps = () => {
    if (!tenantPermissions.value.includes("apps")) {
      return false;
    }

    return !isUnknownHiddenByModuleConfig("apps", "excludeUnknownGroup");
  };
  const canSeePromotions = () =>
    !isUnknownHiddenByModuleConfig("promotions", "excludeUnknownGroup") &&
    hasAllowedRole(["dealer"]);

  const canSeeFavProducts = () =>
    isModuleActiveOrHasPermission("favoriteProducts", "shopFavorites.see") &&
    hasAllowedRole(["dealer"]);

  const canSeeCommitmentLetter = () =>
    isModuleActiveOrHasPermission(
      "commitmentLetter",
      "commitmentLetterPage.see",
    ) && hasAllowedRole(["dealer"]);
  const canSeeKickbackInfo = () => isModuleVisible("kickbackInfo", null);
  const canSeeKickbackPayout = () =>
    isModuleVisible("kickback-payout", null) && hasAllowedRole(["dealer"]);
  const canSeeMailbox = computed(() => {
    if (!isModuleVisible("mailbox", null)) {
      return false;
    }

    return !isUnknownHiddenByModuleConfig("mailbox", "excludeUnknownGroup");
  });
  const canSeeStaff = () =>
    isModuleVisible("staff", null) && hasAllowedRole(["staff"]);
  const canSeeWholesaler = () =>
    hasAllowedRole(["wholesaler", "wholesalerStaff"]);
  const canSeeVouchers = () =>
    isModuleActiveOrHasPermission("vouchers", "voucherExchange.see") &&
    hasAllowedRole(["staff", "wholesaler", "wholesalerStaff"]);
  const canSeeNews = () => isModuleVisible("news", null);
  const canSeeTeaser = () => isModuleVisible("teasers", null);
  const canSeeSlideshow = () => isModuleVisible("slideshow", null);
  const canSeeMainBox = () => isModuleVisible("mainBoxes", null);
  const canSeeFrontendMenu = () => isModuleVisible("frontendMenu", null);
  const canSeeHelpWithLinkTooltip = () =>
    isModuleVisible("helpWithLinkTooltip", null);
  const canSeeHelpTooltip = () => isModuleVisible("helpTooltip", null);

  const getVisibleItems = (matrix) => {
    const context = getRawContext();

    return matrix
      .filter((item) => {
        const show =
          typeof item.showIf === "function" ? item.showIf(context) : true;
        console.log("[MODULE DEBUG]", {
          id: item.id,
          visible: show,
          allowedRoles: item.allowedRoles,
          userRoles: context.userRoles,
          showIfExists: typeof item.showIf === "function",
        });
        return show;
      })
      .map((item) => ({
        name:
          typeof item.nameKey === "string" && t
            ? t(item.nameKey)
            : item.nameKey,
        description:
          typeof item.descriptionKey === "string" && t
            ? t(item.descriptionKey)
            : item.descriptionKey,
        link: typeof item.link === "function" ? item.link(context) : item.link,
        linkText:
          typeof item.linkTextKey === "string" && t
            ? t(item.linkTextKey)
            : item.linkTextKey,
        show: true,
      }));
  };

  // NEW - Return raw context with functions (not values)
  const getRawContext = () => ({
    tenant: tenant.value,
    userRoles: userRoles.value,
    isFirstStopGroup,
    canSeeAboutPage,
    canSeeBTraining,
    canSeeDisplaysFurniture,
    canSeeExternalTraining,
    canSeeFavProducts,
    canSeeIndividualSupport,
    canSeeKickbackExchange,
    canSeeKickbackFeeBox,
    canSeeKickbackInfo,
    canSeeKickbackPayout,
    canSeeMainPageDefaultBanners,
    canSeeMailbox: () => canSeeMailbox.value,
    canSeeMediaDownload,
    canSeePriceBrochures,
    canSeeApps,
    canSeeOnlineTraining,
    // shop
    canSeeShop,
    canSeeShopOrders,
    canSeeShopComingSoon,
    canSeeShopTeaser,
    canSeeCartIcon,
    // points
    canSeePointsBox,
    canSeePointsInfo,
    canSeeSharePointsBtn,

    canSeeProfile,
    canSeePromotions,
    canSeeStaff,
    canSeeWholesaler,
    canSeeUberall,
    canSeeVouchers,
    canSeeWelcomePoints,
    canUsePointsInExternalTraining,
    canSeeNews,
    canSeeTeaser,
    canSeeSlideshow,
    canSeeMainBox,
    canSeeFrontendMenu,
    canSeeHelpWithLinkTooltip,
    canSeeHelpTooltip,
    canSeeCommitmentLetter,
  });

  // NEW - Return evaluated (boolean) context for rendering
  const getEvaluatedContext = () => {
    const raw = getRawContext();
    const evaluated = {};

    for (const key in raw) {
      evaluated[key] = typeof raw[key] === "function" ? raw[key]() : raw[key];
    }

    return evaluated;
  };

  return {
    tenant,
    userRoles,
    canSeeMailbox,
    getContext: getEvaluatedContext,
    hasAllowedRole,
    getVisibleItems,
  };
}
